import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { WarningCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { RejectionRowActions } from "@/components/analytics/RejectionRowActions";
import { RejectionTrendChart } from "@/components/analytics/RejectionTrendChart";

const RANGES = [7, 30, 90];
const pct = (r) => (r == null ? "—" : `${(r * 100).toFixed(1)}%`);
const TH = "px-2 py-1.5 text-right";
const TD = "px-2 py-1.5 text-right font-mono";

function RateTable({ rows, kind, testId, buyerId, onChanged }) {
  if (!rows.length) return null;
  return (
    <div className="min-w-0">
      <div className="label-mono mb-1">{kind === "vendor" ? "By affiliate" : "By funnel"}</div>
      <div className="overflow-x-auto border border-black">
        <table className="w-full min-w-[620px] text-xs">
          <thead>
            <tr className="whitespace-nowrap border-b border-black bg-zinc-100 text-[10px] uppercase tracking-widest">
              <th className="px-2 py-1.5 text-left">{kind === "vendor" ? "Affiliate" : "Funnel"}</th>
              <th className={TH}>Sent</th><th className={TH}>Webhook rejected</th><th className={TH}>Not a case</th>
              <th className={TH}>Duplicate</th><th className={TH}>Rate</th><th className={TH}>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-b border-zinc-200 last:border-b-0 ${r.flagged ? "bg-red-50" : ""}`} data-testid={`${testId}-${r.id}`}>
                <td className="px-2 py-1.5">
                  {r.name}
                  {r.flagged && <span className="ml-1 bg-red-600 px-1 text-[9px] uppercase tracking-widest text-white" data-testid={`${testId}-${r.id}-flag`}>High</span>}
                </td>
                <td className={TD}>{r.sent}</td><td className={TD}>{r.rejected}</td><td className={TD}>{r.not_a_case}</td>
                <td className={TD}>{r.duplicate}</td>
                <td className={`${TD} font-bold ${r.flagged ? "text-red-700" : ""}`}>{pct(r.rate)}</td>
                <td className="px-2 py-1.5 text-right">
                  <RejectionRowActions buyerId={buyerId} kind={kind} row={r} onChanged={onChanged} testId={`${testId}-${r.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BuyerCard({ b, onChanged }) {
  const p = `buyer-rej-${b.buyer_id}`;
  return (
    <div className="brut-card p-4" data-testid={p}>
      <div className="mb-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="font-chivo text-lg font-black">{b.buyer_name}</div>
        <div className="text-xs" data-testid={`${p}-total`}>{b.any} of {b.sent} leads rejected · <b className={b.flagged ? "text-red-700" : ""}>{pct(b.rate)}</b></div>
        <div className="text-[11px] text-zinc-600">Webhook {b.rejected} · Not a case {b.not_a_case} · Duplicate {b.duplicate}</div>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <RateTable rows={b.by_vendor} kind="vendor" testId={`${p}-vendor`} buyerId={b.buyer_id} onChanged={onChanged} />
        <RateTable rows={b.by_funnel} kind="funnel" testId={`${p}-funnel`} buyerId={b.buyer_id} onChanged={onChanged} />
      </div>
    </div>
  );
}

function Flags({ flags, threshold }) {
  if (!flags.length) {
    return <div className="brut-card mb-4 p-3 text-xs text-zinc-600" data-testid="buyer-rej-no-flags">Nothing above {pct(threshold.rate)} with at least {threshold.min_leads} leads. Traffic looks clean.</div>;
  }
  return (
    <div className="mb-4 border-2 border-red-700 bg-red-50 p-3" data-testid="buyer-rej-flags">
      <div className="mb-1 flex items-center gap-2 font-chivo font-black uppercase tracking-tight text-red-800">
        <WarningCircle size={16} weight="bold" /> Needs a look ({flags.length})
      </div>
      {flags.slice(0, 12).map((f) => (
        <div key={`${f.buyer_id}-${f.kind}-${f.id}`} className="text-xs" data-testid={`buyer-rej-flag-${f.buyer_id}-${f.kind}-${f.id}`}>
          <b>{f.buyer_name}</b> · {f.kind === "vendor" ? "Affiliate" : "Funnel"} {f.name}: {f.any} of {f.sent} rejected (<b>{pct(f.rate)}</b>)
        </div>
      ))}
    </div>
  );
}

// Admin-only: each buyer's rejection rate by affiliate and funnel.
export function BuyerRejectionsReport() {
  const [days, setDays] = useState(30);
  const [buyerId, setBuyerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setLoading(true);
    api.get("/_admin/analytics/buyer-rejections", { params: { days, ...(buyerId ? { buyer_id: buyerId } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't load buyer rejections"))
      .finally(() => setLoading(false));
  }, [days, buyerId, reload]);
  const onChanged = () => setReload((n) => n + 1);
  return (
    <div data-testid="buyer-rejections-report">
      <div className="brut-card mb-4 flex flex-wrap items-end gap-4 p-4">
        <div>
          <div className="label-mono">Window</div>
          <div className="mt-1 flex gap-1">
            {RANGES.map((d) => (
              <button key={d} type="button" onClick={() => setDays(d)} data-testid={`buyer-rej-range-${d}`}
                      className={`border border-black px-3 py-1.5 text-xs uppercase tracking-widest transition-colors duration-100 ${days === d ? "bg-black text-white" : "hover:bg-zinc-100"}`}>
                Last {d}d
              </button>
            ))}
          </div>
        </div>
        <label className="min-w-[14rem]">
          <span className="label-mono">Buyer</span>
          <select value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="mt-1 block w-full border border-black px-2 py-1.5 text-sm" data-testid="buyer-rej-buyer">
            <option value="">All buyers</option>
            {(data?.buyer_options || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <p className="max-w-xl text-[11px] text-zinc-600">
          A rejection is the buyer&apos;s webhook saying no, or the buyer marking the lead &ldquo;Not a case&rdquo; or &ldquo;Duplicate&rdquo;.
          Each lead counts once per buyer. Red means {pct(data?.threshold?.rate ?? 0.2)} or more with at least {data?.threshold?.min_leads ?? 10} leads. Admins get a daily email about these.
          Use <b>Pause</b> on a red row to stop that traffic; you can undo it anytime.
        </p>
      </div>
      <RejectionTrendChart buyerId={buyerId} />
      {loading && !data && <div className="label-mono p-6 text-center">Loading…</div>}
      {data && (
        <>
          <Flags flags={data.flags} threshold={data.threshold} />
          <div className="space-y-4">
            {data.buyers.length ? data.buyers.map((b) => <BuyerCard key={b.buyer_id} b={b} onChanged={onChanged} />) : (
              <div className="brut-card p-6 text-center label-mono" data-testid="buyer-rej-empty">No leads sent to buyers in this window.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
